/**
 * Co-sign a pack — F-055 (docs/ROOMS.md §3.3; the reputation-staked endorsement
 * made visible). A first-person action: another producer stakes their name on
 * this pack, and that stake — weighted by their earned standing — feeds the
 * creator's storefront standing. This bar shows the social proof and, for a
 * signed-in producer who isn't the owner, the action itself.
 *
 * Honest states, never a fake one:
 *   • your own pack  → the count, no button (you cannot vouch for yourself);
 *   • already staked → "You co-signed this", static;
 *   • signed out     → the count + a sign-in link;
 *   • otherwise      → the co-sign action.
 *
 * The vouch is amethyst — the jewel/trust voice (CANVAS §2), distinct from the
 * gold of commerce right beside it.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/ui/Toast';
import { Button } from './ui/Button';
import { IconCheck } from './ui/icons';
import { listings, ApiError } from '../lib/api';
import { apiErrorMessage } from '../lib/errors';
import { track } from '../lib/analytics';

interface CosignBarProps {
  listingId: string;
  creatorId: string;
  initialCount: number;
  initialViewerHasCosigned: boolean;
}

export function CosignBar({ listingId, creatorId, initialCount, initialViewerHasCosigned }: CosignBarProps) {
  const { t } = useI18n();
  const { user, status } = useAuth();
  const toast = useToast();

  const [count, setCount] = useState(initialCount);
  const [cosigned, setCosigned] = useState(initialViewerHasCosigned);
  const [pending, setPending] = useState(false);

  const isOwnPack = status === 'authenticated' && user?.id === creatorId;

  async function onCosign() {
    setPending(true);
    try {
      const res = await listings.cosign(listingId);
      setCount(res.cosign_count);
      setCosigned(true);
      track('cosign_added', { listing_id: listingId, creator_id: creatorId });
    } catch (err) {
      // Already co-signed (a stale view) — reconcile to the truth rather than error.
      if (err instanceof ApiError && err.status === 409) {
        setCosigned(true);
        setCount((n) => Math.max(n, 1));
      } else {
        toast.push(apiErrorMessage(err, t), 'error');
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="border-border-subtle mt-4 border-t pt-4">
      <p className="type-label text-secondary">
        {count > 0 ? t.cosign.count(count) : t.cosign.none}
      </p>

      {isOwnPack ? (
        <p className="type-caption text-muted mt-2">{t.cosign.ownPack}</p>
      ) : cosigned ? (
        <p className="type-label text-amethyst-lite mt-3 inline-flex items-center gap-1.5">
          <IconCheck size={16} aria-hidden />
          {t.cosign.done}
        </p>
      ) : status === 'authenticated' ? (
        <div className="mt-3">
          <Button
            variant="secondary"
            size="md"
            block
            loading={pending}
            onClick={onCosign}
            className="border-amethyst text-amethyst-lite hover:bg-amethyst-tint"
          >
            {t.cosign.action}
          </Button>
          <p className="type-caption text-muted mt-2">{t.cosign.explain}</p>
        </div>
      ) : (
        <p className="type-caption text-muted mt-2">
          <Link to="/auth" className="text-amethyst-lite hover:text-primary focus-ring rounded-sm">
            {t.cosign.signInPrompt}
          </Link>
        </p>
      )}
    </div>
  );
}
